import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    SortingState,
} from '@tanstack/react-table';
import { useMemo, useState, useEffect, useCallback } from 'react';
import tableIcon from '../../assets/images/common/table_icon.png';
import copyIcon from '../../assets/images/common/copy.svg';
import checkIcon from '../../assets/images/common/check.svg';
import {
    copyTableToClipboard,
    getRatioColumns,
    getTransitionColumns,
    getDefaultSorting,
    TimeFormat,
    RatioColumnLabels,
    TransitionColumnLabels,
} from './util/Table';
import Select from '../../components/Select';
import { useTranslation } from 'react-i18next';

// Propsの型定義
type Props = {
    value: any[];
    tableType: 'ratio' | 'transition';
};

/**
 * テーブルコンポーネント
 * @param tableType テーブルの種類
 * @param value テーブルのデータ
 * @returns テーブル
 */
export const Table = ({ tableType, value }: Props) => {
    const [sorting, setSorting] = useState<SortingState>(getDefaultSorting(tableType));
    const [timeFormat, setTimeFormat] = useState<TimeFormat>('formatted');
    const [copied, setCopied] = useState(false);
    const [pageSize, setPageSize] = useState<number>(10);
    const { t } = useTranslation('analysis');
    const { t: tCommon } = useTranslation('common');
    const dataLength = value.length;

    const ratioColumnLabels: RatioColumnLabels = useMemo(
        () => ({
            title: t('table.columns.title'),
            percentage: t('table.columns.percentage'),
            totalTime: t('table.columns.totalTime'),
            count: t('table.columns.count'),
            averageTimePerCount: t('table.columns.avgPerCount'),
            averageTimePerDay: t('table.columns.avgPerDay'),
        }),
        [t],
    );

    const transitionColumnLabels: TransitionColumnLabels = useMemo(
        () => ({
            date: t('table.columns.date'),
            time: t('table.columns.time'),
        }),
        [t],
    );

    const formatTime = useCallback(
        (minutes: number, format: TimeFormat) => {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;

            if (format === 'minutes') {
                return `${minutes}`;
            }

            if (format === 'clock') {
                const paddedHours = hours.toString().padStart(2, '0');
                const paddedMinutes = mins.toString().padStart(2, '0');
                return `${paddedHours}:${paddedMinutes}`;
            }

            const parts: string[] = [];
            if (hours > 0) {
                parts.push(tCommon('time.hoursShort', { count: hours }));
            }
            if (mins > 0 || parts.length === 0) {
                parts.push(tCommon('time.minutesShort', { count: mins }));
            }
            // join without space to mimic compact notation like 1h20m / 1時間20分
            return parts.join('');
        },
        [tCommon],
    );

    // 時間表示形式のオプション
    const timeFormatOptions = useMemo(
        () => [
            { value: 'minutes', label: t('table.timeFormats.minutes') },
            { value: 'formatted', label: t('table.timeFormats.formatted') },
            { value: 'clock', label: t('table.timeFormats.clock') },
        ],
        [t],
    );

    const columns = useMemo(() => {
        return tableType === 'ratio'
            ? getRatioColumns(timeFormat, formatTime, ratioColumnLabels)
            : getTransitionColumns(timeFormat, formatTime, transitionColumnLabels);
    }, [tableType, timeFormat, formatTime, ratioColumnLabels, transitionColumnLabels]);

    // 表示件数のオプション
    const pageSizeOptions = useMemo(
        () => [
            { value: '10', label: t('table.pageSizeOption', { count: 10 }) },
            { value: '25', label: t('table.pageSizeOption', { count: 25 }) },
            { value: dataLength.toString(), label: t('table.pageSizeAll') },
        ],
        [t, dataLength],
    );

    const table = useReactTable({
        data: value,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: { pageSize: pageSize },
        },
        manualPagination: false,
    });

    useEffect(() => {
        table.setPageSize(pageSize);
        table.setPageIndex(0);
    }, [pageSize, table]);

    const showPagination = dataLength > 10;

    return (
        <>
            {/* 値が存在しない場合 */}
            {value.length === 0 && (
                <div className="w-full h-full flex flex-col justify-center items-center">
                    <img src={tableIcon} alt="table" className="w-16 h-16 image-protection" />
                    <p className="mt-4 text-center">{t('table.noData')}</p>
                </div>
            )}
            {/* 値が存在する場合 */}
            {value.length > 0 && (
                <div className="w-full p-4 lg:p-6 h-full flex flex-col justify-start overflow-auto">
                    {/* オプション */}
                    <div className="flex flex-col lg:flex-row justify-start items-start lg:items-center mb-6 gap-6">
                        {/* 表示件数 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium mr-2">
                                {t('table.pageSizeLabel')}
                            </label>
                            <Select
                                options={pageSizeOptions}
                                defaultValue={
                                    pageSizeOptions.find(
                                        (opt) => opt.value === pageSize.toString(),
                                    ) || pageSizeOptions[0]
                                }
                                onValueChange={(option) => setPageSize(Number(option.value))}
                                className="text-base w-auto min-w-[100px]"
                            />
                        </div>
                        {/* 時間表示形式 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium mr-2">
                                {t('table.timeFormatLabel')}
                            </label>
                            <Select
                                options={timeFormatOptions}
                                defaultValue={
                                    timeFormatOptions.find((opt) => opt.value === timeFormat) ||
                                    timeFormatOptions[0]
                                }
                                onValueChange={(option) =>
                                    setTimeFormat(option.value as TimeFormat)
                                }
                                className="text-base w-auto min-w-[150px]"
                            />
                        </div>
                        {/* 表をコピー */}
                        <button
                            onClick={() =>
                                copyTableToClipboard(table, timeFormat, formatTime, setCopied)
                            }
                            className="lg:ml-2 lg:px-3 py-1 text-sm rounded hover:bg-gray-100 flex items-center gap-1"
                        >
                            <img
                                src={copied ? checkIcon : copyIcon}
                                alt="copy"
                                className="w-4 h-4"
                            />
                            <span>{copied ? t('table.copy.copied') : t('table.copy.copy')}</span>
                        </button>
                    </div>

                    {/* テーブル */}
                    <div className="border-1 border-gray-300 rounded-md overflow-auto max-h-full">
                        <table className="table-auto w-full">
                            <thead className="bg-gray-100 sticky top-0 z-10">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <th
                                                key={header.id}
                                                onClick={header.column.getToggleSortingHandler()}
                                                className="cursor-pointer p-2 border text-left select-none bg-gray-100 font-normal"
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )}{' '}
                                                <span>
                                                    {header.column.getIsSorted() === 'asc' && '↑'}
                                                    {header.column.getIsSorted() === 'desc' && '↓'}
                                                    {!header.column.getIsSorted() && '↕'}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.map((row, rowIndex) => (
                                    <tr
                                        key={row.id}
                                        className={rowIndex % 2 === 1 ? 'bg-gray-50' : ''}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className="p-2 border">
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ページネーション */}
                    {showPagination && (
                        <div className="flex justify-center items-center mt-4 gap-4">
                            <button
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                                className="px-2 py-1 border rounded disabled:opacity-50"
                            >
                                {t('table.pagination.prev')}
                            </button>
                            <span className="text-sm">
                                {t('table.pageIndicator', {
                                    current: table.getState().pagination.pageIndex + 1,
                                    total: table.getPageCount(),
                                })}
                            </span>
                            <button
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                                className="px-2 py-1 border rounded disabled:opacity-50"
                            >
                                {t('table.pagination.next')}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default Table;
